import { useEffect, useRef, useState } from "react";

const REVEAL_MS = 1800;
const SCRAMBLE_TICK_MS = 42;
export const DEFAULT_MIN_SCRAMBLE_MS = 5000;

const LOWER = "abcdefghijklmnopqrstuvwxyz";
const UPPER = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
const DIGITS = "0123456789";
const SYMBOLS = "_=().,;{}[]//\"'@#+-*<>|:";

const PLACEHOLDER_LINES = [
  "// @version=5",
  "strategy(\"Tessera Compose\", overlay=true, margin_long=100)",
  "var float policyGate = na",
  "longCondition = ta.crossover(ta.sma(close, 14), ta.sma(close, 28))",
  "if policyGate and longCondition",
  "    strategy.entry(\"Long\", strategy.long)",
  "stopPrice = strategy.position_avg_price * (1 - slPct)",
  "strategy.exit(\"Exit\", \"Long\", stop=stopPrice)",
  "// --- Tessera policy injection site ---",
  "tessera_allow_entry = true",
  "plotshape(tessera_allow_entry, title=\"Gate\", style=shape.triangleup)",
];

export type PineScriptScramblePhase = "waiting" | "revealing" | "done";

function pickChar(pool: string): string {
  return pool.charAt(Math.floor(Math.random() * pool.length)) || "?";
}

function randomGlyphFor(char: string): string {
  if (char === " " || char === "\t" || char === "\n" || char === "\r") return char;
  if (/[a-z]/.test(char)) return pickChar(LOWER);
  if (/[A-Z]/.test(char)) return pickChar(UPPER);
  if (/[0-9]/.test(char)) return pickChar(DIGITS);
  return pickChar(SYMBOLS);
}

function scrambleTemplateLine(line: string, intensity: number): string {
  return line
    .split("")
    .map((char) => {
      if (char === " " || char === "\t") return char;
      if (Math.random() > intensity) return char;
      return randomGlyphFor(char);
    })
    .join("");
}

export function buildPlaceholderScramble(): string {
  return PLACEHOLDER_LINES.map((line) => scrambleTemplateLine(line, 0.45 + Math.random() * 0.4)).join(
    "\n"
  );
}

function buildRevealText(code: string, progress: number, tick: number): string {
  const chars = code.split("");
  const total = Math.max(chars.length, 1);

  return chars
    .map((char, index) => {
      if (char === "\n" || char === "\r") return char;
      const threshold = (index / total) * 0.94 + 0.04;
      if (progress >= threshold) return char;
      if (char === " " || char === "\t") return char;
      if (progress > threshold - 0.06 && (index + tick) % 5 > 1) return char;
      return randomGlyphFor(char);
    })
    .join("");
}

function easeOutCubic(t: number): number {
  const inv = 1 - t;
  return 1 - inv * inv * inv;
}

export type UsePineScriptScrambleOptions = {
  enabled: boolean;
  code?: string;
  waitingForCode?: boolean;
  minScrambleMs?: number;
  onRevealComplete?: () => void;
  sessionKey?: string | number;
};

export type UsePineScriptScrambleResult = {
  phase: PineScriptScramblePhase;
  displayText: string;
  busy: boolean;
};

export function usePineScriptScramble({
  enabled,
  code = "",
  waitingForCode = false,
  minScrambleMs = DEFAULT_MIN_SCRAMBLE_MS,
  onRevealComplete,
  sessionKey,
}: UsePineScriptScrambleOptions): UsePineScriptScrambleResult {
  const trimmedCode = code.trim();
  const sessionStartedRef = useRef(performance.now());

  const [phase, setPhase] = useState<PineScriptScramblePhase>("waiting");
  const [displayText, setDisplayText] = useState(buildPlaceholderScramble);

  useEffect(() => {
    if (!enabled) return;
    sessionStartedRef.current = performance.now();
    setPhase("waiting");
    setDisplayText(buildPlaceholderScramble());
  }, [enabled, sessionKey]);

  useEffect(() => {
    if (!enabled || phase !== "waiting") return;
    setDisplayText(buildPlaceholderScramble());
    const id = window.setInterval(() => {
      setDisplayText(buildPlaceholderScramble());
    }, SCRAMBLE_TICK_MS);
    return () => window.clearInterval(id);
  }, [enabled, phase]);

  useEffect(() => {
    if (!enabled || !trimmedCode) return;

    let cancelled = false;
    let raf = 0;
    let revealTimeout = 0;
    let minDelayTimeout = 0;
    let tick = 0;

    const runReveal = () => {
      if (cancelled) return;
      const startedAt = performance.now();
      setPhase("revealing");

      const step = (now: number) => {
        if (cancelled) return;
        tick += 1;
        const linear = Math.min(1, (now - startedAt) / REVEAL_MS);
        const eased = easeOutCubic(linear);
        setDisplayText(buildRevealText(trimmedCode, eased, tick));

        if (linear < 1) {
          raf = window.requestAnimationFrame(step);
          return;
        }

        setDisplayText(trimmedCode);
        setPhase("done");
        revealTimeout = window.setTimeout(() => {
          if (!cancelled) onRevealComplete?.();
        }, 160);
      };

      raf = window.requestAnimationFrame(step);
    };

    const elapsed = performance.now() - sessionStartedRef.current;
    const waitMs = Math.max(0, minScrambleMs - elapsed);
    if (waitMs > 0) {
      minDelayTimeout = window.setTimeout(runReveal, waitMs);
    } else {
      runReveal();
    }

    return () => {
      cancelled = true;
      window.clearTimeout(minDelayTimeout);
      window.cancelAnimationFrame(raf);
      window.clearTimeout(revealTimeout);
    };
  }, [enabled, minScrambleMs, onRevealComplete, trimmedCode]);

  useEffect(() => {
    if (!enabled || waitingForCode || trimmedCode) return;
    setPhase("waiting");
    setDisplayText(buildPlaceholderScramble());
    sessionStartedRef.current = performance.now();
  }, [enabled, trimmedCode, waitingForCode]);

  if (!enabled) {
    return {
      phase: "done",
      displayText: trimmedCode,
      busy: false,
    };
  }

  const bodyText = phase === "done" && trimmedCode ? trimmedCode : displayText;

  return {
    phase,
    displayText: bodyText,
    busy: phase !== "done",
  };
}
